import {
  CONTACT_OPTIONS,
  CONTACT_TIME_OPTIONS,
  freeValuationSchema,
} from '@propertypal/shared/src/constants/instantValuation';
import { selectUser } from '@propertypal/shared/src/reducers/user';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { Agent } from '@propertypal/shared/src/types/agents';
import { ValuationResult } from '@propertypal/shared/src/types/valuations';
import puralise from '@propertypal/shared/src/utils/puralise';
import Button from '@propertypal/web-ui/src/buttons/Button';
import PhoneNumberInput from '@propertypal/web-ui/src/inputs/PhoneNumberInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import TextInput from '@propertypal/web-ui/src/inputs/TextInput';
import { useToast } from '@propertypal/web-ui/src/toast/ToastContext';
import { MidHeading, SubHeading, ItalicText } from '@propertypal/web-ui/src/typography';
import AgentsBox from '@propertypal/web-ui/src/valuations/AgentsBox';
import { useFormik } from 'formik';
import React, { FunctionComponent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from 'styled-components';

interface Props {
  mode: 'agent' | 'instant';
  agentsSelected: Agent[];
  result: ValuationResult;
  onRequestSent: () => void;
  onBackClick: () => void;
}

const RequestValuationForm: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const user = useSelector(selectUser);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { openToast } = useToast();
  const totalAgents = `${props.agentsSelected.length} ${puralise('agent', 'agents', props.agentsSelected.length)}`;

  const sendValuation = async (data: any) => {
    setLoading(true);

    try {
      await axios(
        {
          method: 'POST',
          url: `${API_URL}/valuation/${props.result?.id}/send`,
          data,
        },
        dispatch,
      );

      trackGaEvent('valuation_send', { type: props.mode });

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth',
      });

      props.onRequestSent();
    } catch {
      setLoading(false);
      openToast({
        title: 'Error',
        message: 'Something went wrong. Please try again later.',
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      name: user.details?.name || '',
      phoneNumberPrefix: '+44',
      phoneNumber: user.details?.phoneNumber?.replace(/^0+/, '') ?? '',
      email: user.details?.email || '',
      contactPreference: '',
      contactTimePreference: 'NO_PREFERENCE',
    },
    validationSchema: freeValuationSchema,
    onSubmit: (values) => {
      if (!loading && !!props.agentsSelected.length) {
        sendValuation({
          telephone: `${values.phoneNumberPrefix}${values.phoneNumber}`,
          email: values.email,
          name: values.name,
          contactPreference: values.contactPreference,
          contactTimePreference: values.contactTimePreference,
          accounts: props.agentsSelected.map((a) => a.accountNumber),
        });
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <SubHeading>Request an Agent Valuation</SubHeading>

      <MidHeading color={theme.primary} mt={15} fontSize={16}>
        {totalAgents} selected
      </MidHeading>

      <AgentsBox
        agents={props.agentsSelected}
        align="left"
        backgroundColor={theme.backgroundLighter}
        containerStyle={{ margin: '10px 0 30px' }}
        onBackClick={props.onBackClick}
      />

      <MidHeading color={theme.primary} mt={15} fontSize={16}>
        Summary
      </MidHeading>

      <ItalicText
        mt={10}
        fontSize={16}
        color={theme.textDark}
        dangerouslySetInnerHTML={{ __html: props.result.summaryText }}
      />

      <MidHeading color={theme.primary} mt={30} fontSize={16}>
        Your details
      </MidHeading>

      <TextInput
        containerStyle={{ marginTop: 15 }}
        label="Your name"
        name="name"
        value={formik.values.name}
        onValueChange={formik.handleChange('name')}
        placeholder="Your name"
        error={formik.touched.name && formik.errors.name}
        testID="nameInput"
      />

      <PhoneNumberInput
        containerStyle={{ marginTop: 15 }}
        label="Your telephone number"
        value={formik.values.phoneNumber}
        onValueChange={formik.handleChange('phoneNumber')}
        placeholder="Your telephone number"
        error={formik.touched.phoneNumber && formik.errors.phoneNumber}
        prefix={formik.values.phoneNumberPrefix}
        onPrefixChange={formik.handleChange('phoneNumberPrefix')}
        testID="phoneInput"
      />

      <TextInput
        containerStyle={{ marginTop: 15 }}
        label="Your email address"
        name="email"
        value={formik.values.email}
        onValueChange={formik.handleChange('email')}
        placeholder="Your email address"
        error={formik.touched.email && formik.errors.email}
        testID="emailInput"
      />

      <SelectInput
        containerStyle={{ marginTop: 15 }}
        label="Contact preference"
        ariaLabel="Contact preference"
        id="contactPreference"
        options={CONTACT_OPTIONS}
        value={formik.values.contactPreference}
        onChange={formik.handleChange('contactPreference')}
        placeholder="Please select"
        error={formik.touched.contactPreference && formik.errors.contactPreference}
        testID="contactPreferenceSelect"
      />

      <SelectInput
        containerStyle={{ marginTop: 15 }}
        label="Contact time preference"
        ariaLabel="Contact time preference"
        id="contactTimePreference"
        options={CONTACT_TIME_OPTIONS}
        value={formik.values.contactTimePreference}
        onChange={formik.handleChange('contactTimePreference')}
      />

      <Button
        containerStyle={{ marginTop: 30 }}
        loading={loading}
        type="submit"
        testID="sendDetails"
        disabled={!props.agentsSelected.length}
      >
        Send my details to {totalAgents}
      </Button>
    </form>
  );
};

export default RequestValuationForm;
